'use client';

import { useEffect, useRef, useState } from 'react';

import { REVEAL_ROOT_ATTRS } from '@/constants/heroScene';

/** Opacity at which scrubbed reveal content counts as visible to the user. */
const DISCERNIBLE_OPACITY = 0.95;

/**
 * Cap for the short rAF burst while waiting on a play-once GSAP opacity fade.
 * Avoids a perpetual rAF loop (iOS pauses rAF during touch scroll).
 */
const DISCERNIBLE_RAF_BURST_MAX_FRAMES = 90;

type UseDiscernibleOptions = {
  /** Stop observing after the first visible intersection (default true). */
  once?: boolean;
  /**
   * Fraction of the element that must intersect the viewport (0–1).
   * Ignored when `topAt` is set.
   */
  amount?: number;
  /**
   * Fraction of viewport height from the top (0–1). When set, fires once the
   * element's top edge has scrolled to or past that line (e.g. `0.45` ≈ 45vh),
   * instead of measuring how much of the element is visible.
   */
  topAt?: number;
  /** Skip observers — always treated as in view (e.g. reduced motion). */
  disabled?: boolean;
  /**
   * Opacity under a reveal-root ancestor at which the element counts as
   * discernible. Defaults to {@link DISCERNIBLE_OPACITY} (0.95).
   */
  minOpacity?: number;
};

const REVEAL_ROOT_SELECTOR = REVEAL_ROOT_ATTRS.map((attr) => `[${attr}]`).join(', ');

function closestRevealRoot(element: Element): Element | null {
  return element.closest(REVEAL_ROOT_SELECTOR);
}

function isElementDiscernible(
  element: Element,
  minOpacity: number = DISCERNIBLE_OPACITY,
): boolean {
  const revealRoot = closestRevealRoot(element);
  let node: Element | null = element;

  while (node) {
    const style = getComputedStyle(node);
    if (style.visibility === 'hidden') return false;

    if (revealRoot && Number.parseFloat(style.opacity) < minOpacity) {
      return false;
    }

    if (node === revealRoot) break;
    node = node.parentElement;
  }

  return true;
}

function meetsViewportAmount(element: Element, amount: number): boolean {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;

  const visibleTop = Math.max(rect.top, 0);
  const visibleBottom = Math.min(rect.bottom, window.innerHeight);
  const visibleLeft = Math.max(rect.left, 0);
  const visibleRight = Math.min(rect.right, window.innerWidth);

  if (visibleBottom <= visibleTop || visibleRight <= visibleLeft) return false;

  const visibleArea = (visibleBottom - visibleTop) * (visibleRight - visibleLeft);
  return visibleArea / (rect.width * rect.height) >= amount;
}

/** True when the element's top edge is at or above `topAt × viewport height`. */
function meetsTopAt(element: Element, topAt: number): boolean {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  return rect.top <= window.innerHeight * topAt;
}

function meetsGeometry(
  element: Element,
  { amount, topAt }: { amount: number; topAt?: number },
): boolean {
  if (topAt !== undefined) return meetsTopAt(element, topAt);
  return meetsViewportAmount(element, amount);
}

function subscribeDiscernible(
  element: Element,
  {
    once,
    amount,
    topAt,
    minOpacity,
    onVisible,
  }: {
    once: boolean;
    amount: number;
    topAt?: number;
    minOpacity: number;
    onVisible: () => void;
  },
): () => void {
  let settled = false;
  let rafBurstId = 0;
  let rafBurstFrames = 0;
  const geometry = { amount, topAt };

  const stopRafBurst = () => {
    cancelAnimationFrame(rafBurstId);
    rafBurstId = 0;
    rafBurstFrames = 0;
  };

  const stop = () => {
    stopRafBurst();
    observer.disconnect();
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('scrollend', onScrollEnd);
  };

  const evaluate = () => {
    if (settled) return true;
    if (
      !isElementDiscernible(element, minOpacity) ||
      !meetsGeometry(element, geometry)
    ) {
      return false;
    }

    onVisible();
    settled = true;
    if (once) stop();
    return true;
  };

  /**
   * When the element is on-screen but a reveal-root ancestor is still fading
   * (play-once GSAP), poll briefly so children unblock without a perpetual rAF
   * loop during touch scroll.
   */
  const startRafBurstIfNeeded = () => {
    if (settled || rafBurstId !== 0) return;
    if (!meetsGeometry(element, geometry)) return;
    if (isElementDiscernible(element, minOpacity)) {
      evaluate();
      return;
    }

    const tick = () => {
      rafBurstFrames += 1;
      if (evaluate()) return;
      if (rafBurstFrames >= DISCERNIBLE_RAF_BURST_MAX_FRAMES) {
        stopRafBurst();
        return;
      }
      rafBurstId = requestAnimationFrame(tick);
    };

    rafBurstId = requestAnimationFrame(tick);
  };

  const observer = new IntersectionObserver(
    () => {
      if (evaluate()) return;
      startRafBurstIfNeeded();
    },
    { threshold: [0, 0.25, 0.5, 0.75, 1] },
  );

  observer.observe(element);

  const onScroll = () => {
    // Fresh burst each scroll sample while waiting on opacity — reset the
    // frame budget so a long scrub fade can still finish after the user stops.
    if (rafBurstId !== 0) stopRafBurst();
    if (evaluate()) return;
    startRafBurstIfNeeded();
  };

  const onScrollEnd = () => {
    if (evaluate()) return;
    startRafBurstIfNeeded();
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('scrollend', onScrollEnd, { passive: true });

  if (evaluate()) {
    return () => {
      stop();
    };
  }
  startRafBurstIfNeeded();

  return () => {
    stop();
  };
}

/**
 * Viewport visibility that ignores elements hidden by ancestor opacity /
 * visibility — e.g. `[data-hero-reveal]` / `[data-section-body-reveal]` targets
 * mid GSAP reveal. Re-checks on scroll / scrollend and, only while on-screen
 * but waiting on a reveal fade, a short capped rAF burst (not a perpetual loop).
 */
export function useDiscernibleInView<T extends Element = HTMLElement>({
  once = true,
  amount = 0.5,
  topAt,
  disabled = false,
  minOpacity = DISCERNIBLE_OPACITY,
}: UseDiscernibleOptions = {}) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (disabled) return;

    const element = ref.current;
    if (!element) return;

    return subscribeDiscernible(element, {
      once,
      amount,
      topAt,
      minOpacity,
      onVisible: () => setInView(true),
    });
  }, [amount, disabled, minOpacity, once, topAt]);

  return { ref, inView: disabled || inView };
}

/**
 * Same discernibility rules as `useDiscernibleInView`, but for an element
 * found via `document.querySelector` (e.g. a sibling reveal root).
 */
export function useDiscernibleSelector(
  selector: string,
  {
    once = true,
    amount = 0.5,
    topAt,
    disabled = false,
    minOpacity = DISCERNIBLE_OPACITY,
  }: UseDiscernibleOptions = {},
) {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (disabled) return;

    const element = document.querySelector(selector);
    if (!element) return;

    return subscribeDiscernible(element, {
      once,
      amount,
      topAt,
      minOpacity,
      onVisible: () => setInView(true),
    });
  }, [amount, disabled, minOpacity, once, selector, topAt]);

  return disabled || inView;
}
